import { useCallback, useEffect, useRef, useState } from 'react';
import {
    FabPoint, clampToViewport, defaultPosition, exceedsDragThreshold,
    parseStoredPosition, panelSide, panelVertical, snapToNearestEdge, panelAnchor,
    FAB_STORAGE_KEY,
} from '../utils/fabPosition';

interface Options {
    /** Square edge of the button, px. */
    size: number;
    /** Distance from the corner it rests at before it has ever been moved. */
    inset?: number;
    storageKey?: string;
}

/**
 * Makes the floating Raza AI button draggable, and remembers where it was left.
 *
 * Pointer Events rather than mouse/touch pairs: one code path covers mouse,
 * finger and stylus, and `setPointerCapture` keeps the drag alive when the
 * pointer outruns the button — which it always does on a fast flick.
 */
export function useDraggableFab({ size, inset = 24, storageKey = FAB_STORAGE_KEY }: Options) {
    const [position, setPosition] = useState<FabPoint | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Drag bookkeeping lives in refs: pointermove fires far faster than React
    // re-renders, and reading this from state would lag a frame behind.
    const origin = useRef<FabPoint>({ x: 0, y: 0 });
    const grabOffset = useRef<FabPoint>({ x: 0, y: 0 });
    const moved = useRef(false);

    const viewport = () => ({ width: window.innerWidth, height: window.innerHeight });

    // Restore on mount. Deliberately not during render: on the server, and
    // before hydration, there is no window to measure against.
    useEffect(() => {
        let stored: FabPoint | null = null;
        try {
            stored = parseStoredPosition(window.localStorage.getItem(storageKey));
        } catch {
            // Private browsing can throw on localStorage access. A button that
            // forgets where it was is better than a page that will not load.
        }
        // Snap on restore too: a position saved before the button was
        // edge-constrained would otherwise reappear floating mid-screen.
        setPosition(snapToNearestEdge(stored ?? defaultPosition(size, viewport(), inset), size, viewport()));
    }, [size, inset, storageKey]);

    // Rotating a phone or shrinking a window can leave the button off-screen,
    // where it cannot be dragged back. Re-clamp whenever the viewport changes.
    useEffect(() => {
        const onResize = () => {
            setPosition(p => (p ? snapToNearestEdge(p, size, viewport()) : p));
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, [size]);

    const persist = useCallback((p: FabPoint) => {
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(p));
        } catch {
            // See above — losing the saved spot is not worth an exception.
        }
    }, [storageKey]);

    const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
        // Left button / primary contact only: a right-click should open the
        // context menu, not start dragging the thing.
        if (e.button !== 0) return;
        const rect = e.currentTarget.getBoundingClientRect();
        origin.current = { x: e.clientX, y: e.clientY };
        grabOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        moved.current = false;
        e.currentTarget.setPointerCapture(e.pointerId);
    }, []);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
        if (!e.currentTarget.hasPointerCapture?.(e.pointerId)) return;

        const current = { x: e.clientX, y: e.clientY };
        if (!moved.current && !exceedsDragThreshold(origin.current, current)) return;

        if (!moved.current) {
            moved.current = true;
            setIsDragging(true);
        }
        // Stops the page scrolling under the finger mid-drag on touch.
        e.preventDefault();
        setPosition(clampToViewport(
            { x: current.x - grabOffset.current.x, y: current.y - grabOffset.current.y },
            size,
            viewport(),
        ));
    }, [size]);

    const endDrag = useCallback((e: React.PointerEvent<HTMLElement>) => {
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        if (moved.current) {
            setIsDragging(false);
            // Settle onto the nearest edge. The drag itself stays free-form —
            // dragging along a rail feels stuck — but the button only ever
            // RESTS on an edge, never over the content being read.
            setPosition(p => {
                if (!p) return p;
                const snapped = snapToNearestEdge(p, size, viewport());
                persist(snapped);
                return snapped;
            });
        }
    }, [persist, size]);

    /**
     * Swallow the click that follows a drag.
     *
     * A pointerup always fires a click, so without this every drag would also
     * open the chat panel on release.
     */
    const onClickCapture = useCallback((e: React.MouseEvent) => {
        if (moved.current) {
            e.preventDefault();
            e.stopPropagation();
            moved.current = false;
        }
    }, []);

    const reset = useCallback(() => {
        const p = snapToNearestEdge(defaultPosition(size, viewport(), inset), size, viewport());
        setPosition(p);
        persist(p);
    }, [size, inset, persist]);

    // Until the first effect runs there is no measured position; the caller
    // keeps the button hidden for that one frame rather than flashing it in the
    // wrong corner.
    const ready = position !== null;

    return {
        ready,
        isDragging,
        position,
        reset,
        /** Inline style pinning the button to its point. */
        style: ready
            ? ({
                position: 'fixed',
                left: position!.x,
                top: position!.y,
                right: 'auto',
                bottom: 'auto',
                // touchAction none: the browser must not claim the gesture as a
                // page scroll before the drag handler sees it.
                touchAction: 'none',
                // Animate the settle, never the drag: a transition while the
                // finger is down makes the button lag behind it.
                transition: isDragging ? 'none' : 'left 180ms ease-out, top 180ms ease-out',
            } as const)
            : ({ position: 'fixed', visibility: 'hidden' } as const),
        handlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onClickCapture,
        },
        /** Where a panel attached to this button should grow. */
        side: ready ? panelSide(position!, size, viewport()) : ('right' as const),
        vertical: ready ? panelVertical(position!, size, viewport()) : ('up' as const),
        /** Absolute placement for a panel that should hang off the button,
         *  centred on it and clamped to stay on screen. */
        anchorPanel: (panelWidth: number) =>
            ready ? panelAnchor(position!, size, viewport(), panelWidth) : null,
    };
}
